/**
 * Centralized sidebar configuration, keyed by role.
 *
 * To change a role's navigation, edit the array here — no need to touch DashboardPage.
 * `SIDEBAR_CONFIG[role]` returns { title, items, highlight? } or null if the role has no dedicated sidebar.
 * `COMMON_ITEMS` always render at the top.
 */
import {
  Heart,
  BarChart3,
  Building2,
  Users,
  MessageSquare,
  Calendar,
  Briefcase,
  Wrench,
  MapPin,
  GitBranch,
  UserCheck,
  TrendingUp,
  UserCog,
  UserPlus,
  Shield,
  Home,
  FolderKanban,
  CalendarDays,
  Wallet,
  BookOpen,
  Banknote,
  Settings,
  Receipt,
  Wallet as WalletIcon,
} from "lucide-react";

export const COMMON_ITEMS = [
  { path: "/dashboard", label: "Overview", icon: BarChart3 },
  { path: "/dashboard/saved", label: "Saved Properties", icon: Heart },
];

const AGENT_ITEMS = [
  { path: "/dashboard/properties", label: "My Properties", icon: Building2 },
  { path: "/dashboard/analytics", label: "Analytics", icon: TrendingUp },
  { path: "/dashboard/inquiries", label: "Inquiries", icon: MessageSquare },
  { path: "/dashboard/tours", label: "Tour Bookings", icon: Calendar },
  { path: "/dashboard/leads", label: "Leads", icon: Users },
  { path: "/dashboard/deals", label: "Deals", icon: Briefcase },
  { path: "/dashboard/maintenance", label: "Maintenance", icon: Wrench },
];

const ADMIN_ITEMS = [
  { path: "/dashboard/admin/regions", label: "Regions", icon: MapPin },
  { path: "/dashboard/admin/branches", label: "Organization", icon: GitBranch },
  { path: "/dashboard/admin/users", label: "User Management", icon: UserCheck },
  { path: "/dashboard/admin/analytics", label: "Analytics", icon: TrendingUp },
  { path: "/dashboard/admin/agent-performance", label: "Agent Performance", icon: UserCog },
  { path: "/dashboard/agents", label: "Manage Agents", icon: Users },
  { path: "/dashboard/access-control", label: "Access Control", icon: Shield },
  { path: "/dashboard/admin/applications", label: "Agent Applications", icon: UserPlus },
  { path: "/dashboard/admin/all-properties", label: "All Properties", icon: Building2 },
  { path: "/dashboard/admin/all-leads", label: "All Leads", icon: Users },
  { path: "/dashboard/admin/all-deals", label: "All Deals", icon: Briefcase },
  { path: "/dashboard/admin/rentals", label: "Rental Management", icon: Home },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
  { path: "/dashboard/admin/projects", label: "Project Management", icon: FolderKanban },
  { path: "/dashboard/admin/leave", label: "Leave Management", icon: CalendarDays },
  { path: "/dashboard/admin/finance", label: "Finance", icon: Wallet },
  { path: "/dashboard/admin/accounting", label: "Accounting", icon: BookOpen },
  { path: "/dashboard/admin/payroll", label: "Payroll", icon: Banknote },
  { path: "/dashboard/admin/broker", label: "Broker Escrow", icon: Banknote },
  { path: "/dashboard/admin/broker-wallet", label: "Broker Wallet", icon: WalletIcon },
  { path: "/dashboard/admin/broker-applications", label: "Broker Applications", icon: UserPlus },
  { path: "/dashboard/admin/business-settings", label: "Business Settings", icon: Settings },
];

const CEO_ITEMS = [
  { path: "/dashboard/executive", label: "Executive Dashboard", icon: TrendingUp },
  { path: "/dashboard/admin/analytics", label: "Company Analytics", icon: BarChart3 },
  { path: "/dashboard/admin/finance", label: "Finance Overview", icon: Wallet },
  { path: "/dashboard/admin/branches", label: "Organization", icon: GitBranch },
  { path: "/dashboard/admin/all-deals", label: "All Deals", icon: Briefcase },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
];

const REGIONAL_MANAGER_ITEMS = [
  { path: "/dashboard/regional", label: "My Region", icon: BarChart3 },
  { path: "/dashboard/regional/branches", label: "My Branches", icon: GitBranch },
  { path: "/dashboard/admin/all-properties", label: "All Properties", icon: Building2 },
  { path: "/dashboard/admin/all-leads", label: "All Leads", icon: Users },
  { path: "/dashboard/admin/all-deals", label: "All Deals", icon: Briefcase },
  { path: "/dashboard/inquiries", label: "Inquiries", icon: MessageSquare },
  { path: "/dashboard/tours", label: "Tour Bookings", icon: Calendar },
  { path: "/dashboard/admin/rentals", label: "Rentals", icon: Home },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
  { path: "/dashboard/admin/agent-performance", label: "Agent Performance", icon: UserCog },
  { path: "/dashboard/regional/report", label: "Regional Report", icon: TrendingUp },
];

const BRANCH_MANAGER_ITEMS = [
  { path: "/dashboard/branch", label: "My Branch", icon: BarChart3 },
  { path: "/dashboard/branch/agents", label: "My Team", icon: Users },
  { path: "/dashboard/admin/all-properties", label: "Properties", icon: Building2 },
  { path: "/dashboard/admin/all-leads", label: "Leads", icon: Users },
  { path: "/dashboard/admin/all-deals", label: "Deals", icon: Briefcase },
  { path: "/dashboard/inquiries", label: "Inquiries", icon: MessageSquare },
  { path: "/dashboard/tours", label: "Tour Bookings", icon: Calendar },
  { path: "/dashboard/admin/rentals", label: "Rental Management", icon: Home },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
  { path: "/dashboard/admin/agent-performance", label: "Agent Performance", icon: UserCog },
  { path: "/dashboard/admin/petty-cash", label: "Petty Cash", icon: Receipt },
  { path: "/dashboard/branch/report", label: "Branch Report", icon: TrendingUp },
];

const PEOPLE_CULTURE_ITEMS = [
  { path: "/dashboard/admin/agent-performance", label: "Agent Performance", icon: TrendingUp },
  { path: "/dashboard/admin/users", label: "User Management", icon: UserCheck },
  { path: "/dashboard/agents", label: "Manage Agents", icon: Users },
  { path: "/dashboard/admin/applications", label: "Agent Applications", icon: UserPlus },
  { path: "/dashboard/admin/leave", label: "Leave Management", icon: CalendarDays },
];

const LETTING_MANAGER_ITEMS = [
  { path: "/dashboard/team", label: "My Team", icon: Users },
  { path: "/dashboard/admin/all-properties", label: "All Properties", icon: Building2 },
  { path: "/dashboard/admin/rentals", label: "Rental Management", icon: Home },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
  { path: "/dashboard/admin/agent-performance", label: "Agent Performance", icon: UserCog },
  { path: "/dashboard/agents", label: "Manage Agents", icon: Users },
];

const SALES_MANAGER_ITEMS = [
  { path: "/dashboard/team", label: "My Team", icon: Users },
  { path: "/dashboard/admin/all-deals", label: "All Deals", icon: Briefcase },
  { path: "/dashboard/admin/all-leads", label: "All Leads", icon: Users },
  { path: "/dashboard/inquiries", label: "All Inquiries", icon: MessageSquare },
  { path: "/dashboard/tours", label: "Tour Bookings", icon: Calendar },
  { path: "/dashboard/admin/agent-performance", label: "Agent Performance", icon: UserCog },
];

const DATA_ENTRY_ITEMS = [
  { path: "/dashboard/admin/all-properties", label: "Properties", icon: Building2 },
  { path: "/dashboard/admin/rentals", label: "Rentals", icon: Home },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
  { path: "/dashboard/admin/petty-cash", label: "Petty Cash", icon: Receipt },
  { path: "/dashboard/admin/accounting", label: "Accounting", icon: BookOpen },
];

const ACCOUNTS_ITEMS = [
  { path: "/dashboard/team", label: "My Team", icon: Users },
  { path: "/dashboard/admin/rentals", label: "Rental Management", icon: Home },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
  { path: "/dashboard/admin/petty-cash", label: "Petty Cash", icon: Receipt },
  { path: "/dashboard/admin/broker", label: "Broker Escrow", icon: Banknote },
  { path: "/dashboard/admin/broker-wallet", label: "Broker Wallet", icon: WalletIcon },
  { path: "/dashboard/admin/broker-applications", label: "Broker Applications", icon: UserPlus },
  { path: "/dashboard/admin/all-deals", label: "All Deals", icon: Briefcase },
  { path: "/dashboard/admin/leave", label: "Leave Management", icon: CalendarDays },
  { path: "/dashboard/admin/finance", label: "Finance", icon: Wallet },
  { path: "/dashboard/admin/accounting", label: "Accounting", icon: BookOpen },
  { path: "/dashboard/admin/payroll", label: "Payroll", icon: Banknote },
];

const BROKER_ITEMS = [
  { path: "/dashboard/broker", label: "My Transactions", icon: Banknote },
  { path: "/dashboard/broker/wallet", label: "My Wallet", icon: WalletIcon },
];

const PROJECT_MANAGER_ITEMS = [
  { path: "/dashboard/admin/projects", label: "Project Management", icon: FolderKanban },
  { path: "/dashboard/admin/maintenance", label: "Maintenance", icon: Wrench },
];

const CONSTRUCTOR_ITEMS = [
  { path: "/dashboard/admin/projects", label: "Project Management", icon: FolderKanban },
];

/**
 * role → { title, items, highlight }
 * `highlight: true` = section renders with primary accent (used for admin/CEO).
 */
export const SIDEBAR_CONFIG = {
  admin: { title: "Admin Panel", items: ADMIN_ITEMS, highlight: true, extraItems: AGENT_ITEMS, extraTitle: "Agent Tools" },
  ceo: { title: "Executive", items: CEO_ITEMS, highlight: true },
  regional_manager: { title: "Regional Manager", items: REGIONAL_MANAGER_ITEMS },
  branch_manager: { title: "Branch Manager", items: BRANCH_MANAGER_ITEMS },
  people_culture: { title: "People & Culture", items: PEOPLE_CULTURE_ITEMS },
  letting_manager: { title: "Letting Manager", items: LETTING_MANAGER_ITEMS },
  sales_manager: { title: "Sales Manager", items: SALES_MANAGER_ITEMS },
  project_manager: { title: "Project Manager", items: PROJECT_MANAGER_ITEMS },
  constructor: { title: "Constructor", items: CONSTRUCTOR_ITEMS },
  data_entry: { title: "Data Entry", items: DATA_ENTRY_ITEMS },
  accounts: { title: "Accounts", items: ACCOUNTS_ITEMS },
  broker: { title: "Broker", items: BROKER_ITEMS },
  agent: { title: "Agent Tools", items: AGENT_ITEMS },
};

export function getSidebarForRole(role) {
  return SIDEBAR_CONFIG[role] || null;
}
